import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  FileText, 
  Download, 
  Calendar, 
  Users, 
  DollarSign, 
  BarChart3,
  Filter,
  Search,
  Printer,
  IndianRupee
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form-input'
import { Badge } from '@/components/ui/badge'

interface ReportTemplate {
  id: string
  title: string
  description: string
  category: string
  icon: React.ReactNode
  lastGenerated?: string
  frequency: string
}

const CollegeReports = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const reportTemplates: ReportTemplate[] = [
    {
      id: 'student-enrollment',
      title: 'Student Enrollment Report',
      description: 'Comprehensive list of enrolled students with course details',
      category: 'students',
      icon: <Users className="h-5 w-5 text-blue-600" />,
      lastGenerated: '2025-06-10',
      frequency: 'Weekly'
    },
    {
      id: 'fee-collection',
      title: 'Fee Collection Summary',
      description: 'Monthly fee collection status and pending payments',
      category: 'financial',
      icon: <IndianRupee className="h-5 w-5 text-green-600" />,
      lastGenerated: '2025-06-14',
      frequency: 'Monthly'
    },
    {
      id: 'payment-transactions',
      title: 'Payment Transactions',
      description: 'Detailed transaction history with payment methods',
      category: 'financial',
      icon: <BarChart3 className="h-5 w-5 text-purple-600" />,
      lastGenerated: '2025-06-13',
      frequency: 'Daily'
    },
    {
      id: 'course-wise-analysis',
      title: 'Course-wise Analysis',
      description: 'Student distribution and performance by course',
      category: 'academic',
      icon: <BarChart3 className="h-5 w-5 text-orange-600" />,
      lastGenerated: '2025-06-12',
      frequency: 'Monthly'
    },
    {
      id: 'staff-performance',
      title: 'Staff Performance Report',
      description: 'Staff activity and performance metrics',
      category: 'administrative',
      icon: <Users className="h-5 w-5 text-indigo-600" />,
      lastGenerated: '2025-06-11',
      frequency: 'Quarterly'
    },
    {
      id: 'financial-summary',
      title: 'Financial Summary',
      description: 'Complete financial overview with revenue analysis',
      category: 'financial',
      icon: <IndianRupee className="h-5 w-5 text-emerald-600" />,
      lastGenerated: '2025-06-15',
      frequency: 'Monthly'
    },
    {
      id: 'attendance-report',
      title: 'Attendance Report',
      description: 'Student attendance tracking and statistics',
      category: 'academic',
      icon: <Calendar className="h-5 w-5 text-red-600" />,
      lastGenerated: '2025-06-09',
      frequency: 'Weekly'
    },
    {
      id: 'defaulter-list',
      title: 'Fee Defaulters List',
      description: 'Students with pending or overdue payments',
      category: 'financial',
      icon: <FileText className="h-5 w-5 text-yellow-600" />,
      lastGenerated: '2025-06-14',
      frequency: 'Weekly'
    }
  ]

  const categories = [
    { id: 'all', label: 'All Reports', count: reportTemplates.length },
    { id: 'financial', label: 'Financial', count: reportTemplates.filter(r => r.category === 'financial').length },
    { id: 'students', label: 'Students', count: reportTemplates.filter(r => r.category === 'students').length },
    { id: 'academic', label: 'Academic', count: reportTemplates.filter(r => r.category === 'academic').length },
    { id: 'administrative', label: 'Administrative', count: reportTemplates.filter(r => r.category === 'administrative').length }
  ]

  // Filter reports based on category and search
  const filteredReports = reportTemplates.filter(report => {
    const matchesCategory = selectedCategory === 'all' || report.category === selectedCategory
    const matchesSearch = searchTerm === '' || 
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesCategory && matchesSearch
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN')
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'financial':
        return 'bg-green-100 text-green-800'
      case 'students':
        return 'bg-blue-100 text-blue-800'
      case 'academic':
        return 'bg-purple-100 text-purple-800'
      case 'administrative':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getFrequencyColor = (frequency: string) => {
    switch (frequency.toLowerCase()) {
      case 'daily':
        return 'bg-red-100 text-red-800'
      case 'weekly':
        return 'bg-yellow-100 text-yellow-800'
      case 'monthly':
        return 'bg-blue-100 text-blue-800'
      case 'quarterly':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Generate and download comprehensive college reports</p>
        </div>
        
        <Button className="flex items-center bg-green-600 hover:bg-green-700">
          <FileText className="h-4 w-4" />
          <span>Custom Report</span>
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Reports</p>
                <p className="text-2xl font-bold text-gray-900">{reportTemplates.length}</p>
              </div>
              <FileText className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Generated Today</p>
                <p className="text-2xl font-bold text-blue-600">5</p>
              </div>
              <Download className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-purple-600">23</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Scheduled</p>
                <p className="text-2xl font-bold text-orange-600">12</p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label} ({category.count})
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report, index) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card className="h-full hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      {report.icon}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{report.title}</CardTitle>
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge 
                          variant="outline" 
                          className={getCategoryColor(report.category)}
                        >
                          {report.category}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className={getFrequencyColor(report.frequency)}
                        >
                          {report.frequency}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">
                  {report.description}
                </CardDescription>
                
                {report.lastGenerated && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                    <Calendar className="h-4 w-4" />
                    <span>Last generated: {formatDate(report.lastGenerated)}</span>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Button 
                    size="sm" 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Generate
                  </Button>
                  <Button variant="outline" size="sm">
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredReports.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              No reports found matching your criteria.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recent Downloads */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Downloads</CardTitle>
          <CardDescription>
            Your recently generated report downloads
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { name: 'Fee Collection Summary - June 2025', date: '2025-06-15', size: '2.3 MB' },
              { name: 'Student Enrollment Report', date: '2025-06-14', size: '1.8 MB' },
              { name: 'Payment Transactions - Week 24', date: '2025-06-13', size: '4.1 MB' },
              { name: 'Course-wise Analysis Q2', date: '2025-06-12', size: '3.2 MB' }
            ].map((download, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{download.name}</p>
                    <p className="text-sm text-gray-500">
                      Generated on {formatDate(download.date)} • {download.size}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CollegeReports
